import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { Save, AlertCircle, Medal } from 'lucide-react';
import { RankReward } from '../../types';

export const RankManager: React.FC = () => {
    const [rewards, setRewards] = useState<Record<number, RankReward>>({});
    const [isSaving, setIsSaving] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const rankNames: Record<number, string> = {
        1: 'Recruta Arquivista (0 a 39 pts)',
        2: 'Investigador Auxiliar (40 a 99 pts)',
        3: 'Detetive Particular (100 a 249 pts)',
        4: 'Inspetor de 1ª Classe (250 a 499 pts)',
        5: 'Detetive Chefe (500 a 999 pts)',
        6: 'Comissário (1000+ pts)'
    };

    useEffect(() => {
        const fetchRewards = async () => {
            try {
                if (!db) return;
                const snap = await getDoc(doc(db, 'settings', 'ranks'));
                if (snap.exists()) {
                    const data = snap.data();
                    if (data.rewards) {
                        setRewards(data.rewards);
                    }
                }
            } catch (err) {
                console.error("Erro ao carregar configurações de patentes", err);
            } finally {
                setIsLoading(false);
            }
        };
        fetchRewards();
    }, []);

    const handleSave = async () => {
        setIsSaving(true);
        setMessage(null);
        try {
            if (!db) throw new Error("Firebase não conectado.");
            await setDoc(doc(db, 'settings', 'ranks'), { rewards }, { merge: true });
            setMessage({ type: 'success', text: 'Configurações de Patentes Salvas com Sucesso!' });
        } catch (err: any) {
            console.error(err);
            setMessage({ type: 'error', text: 'Erro ao salvar: ' + err.message });
        } finally {
            setIsSaving(false);
        }
    };

    const handleChange = (level: number, field: 'rewardType' | 'rewardAmount', value: string | number) => {
        setRewards(prev => {
            const current = prev[level] || { level, rewardType: 'lupas', rewardAmount: 0 };
            return {
                ...prev,
                [level]: { ...current, [field]: value }
            };
        });
    };

    if (isLoading) return <div className="p-8 text-neutral-400">Carregando configurações...</div>;

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center bg-neutral-900 p-6 flex-col md:flex-row gap-4 border-b border-neutral-800">
                <div>
                    <h2 className="text-2xl font-mono font-bold text-white flex items-center gap-2">
                        <Medal className="w-6 h-6 text-amber-500" />
                        Configurações de Patentes (Rankings)
                    </h2>
                    <p className="text-sm text-neutral-400 mt-1">
                        Defina as recompensas concedidas quando o jogador atinge uma nova patente na Agência. A patente 1 é inicial (sem prêmio).
                    </p>
                </div>
                <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="bg-amber-700 hover:bg-amber-600 disabled:opacity-50 text-white px-6 py-2.5 rounded-lg font-bold flex items-center gap-2 transition-colors whitespace-nowrap"
                >
                    <Save size={18} />
                    {isSaving ? 'Salvando...' : 'Salvar Configurações'}
                </button>
            </div>

            {message && (
                <div className={`mx-6 p-4 rounded-lg flex items-center gap-3 ${message.type === 'success' ? 'bg-green-100 text-green-800 border border-green-300' : 'bg-red-100 text-red-800 border border-red-300'}`}>
                    <AlertCircle size={20} />
                    <p className="font-medium">{message.text}</p>
                </div>
            )}

            <div className="mx-6 grid grid-cols-1 gap-4 pb-12">
                {[2, 3, 4, 5, 6].map(level => {
                    const reward = rewards[level] || { level, rewardType: 'lupas', rewardAmount: 0 };

                    return (
                        <div key={level} className="bg-neutral-900 border border-neutral-800 rounded-lg p-5 flex flex-col md:flex-row md:items-center gap-6">
                            <div className="md:w-1/3">
                                <h3 className="text-lg font-bold text-gray-200">Patente Nível {level}</h3>
                                <p className="text-sm text-amber-500 font-mono mt-1">{rankNames[level]}</p>
                            </div>

                            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-neutral-400 mb-1">Tipo de Recompensa</label>
                                    <select
                                        value={reward.rewardType || 'lupas'}
                                        onChange={(e) => handleChange(level, 'rewardType', e.target.value as any)}
                                        className="w-full bg-neutral-950 border border-neutral-800 rounded px-3 py-2 text-sm text-white focus:border-amber-500 outline-none"
                                    >
                                        <option value="lupas">Lupas Extras</option>
                                        <option value="keys">Chaves de Arquivo</option>
                                        <option value="vip_days">Dias VIP (Passe Livre)</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-neutral-400 mb-1">
                                        {reward.rewardType === 'vip_days' ? 'Dias Concedidos' : 'Quantidade Consumível'}
                                    </label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={reward.rewardAmount || 0}
                                        onChange={(e) => handleChange(level, 'rewardAmount', parseInt(e.target.value) || 0)}
                                        className="w-full bg-neutral-950 border border-neutral-800 rounded px-3 py-2 text-sm text-white focus:border-amber-500 outline-none"
                                    />
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
